import React, { useEffect, useState } from "react";
import LookupManagementComponent from "./LookupManagement.js";
import Loading from "../../components/shared/Loading";
import {
  getAllProvinces,
  getAllInvoiceTypes,
  getAllAdditionalBuyerIdTypes,
  updateProvince,
  getAllCurrencies,
  updateCurrency,
  toggleCurrencyStatus,
  addNewCurrency,
  updateInvoiceType,
  toggleInvoiceTypeStatus,
  addNewInvoiceType,
  addNewAdditionalBuyerIdType,
  toggleAdditionalBuyerIdTypeStatus,
  updateAdditionalBuyerIdType,
  getAllIsic4,
  updateIsic4,
  toggleIsic4Status,
  addNewIsic4
} from "../../api/methods";
import {showToast} from "../../helpers/showToast";

const LookupManagementContainer = ({ tab, ...props }) => {
  const [loading, setLoading] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [invoiceTypes, setInvoiceTypes] = useState([]);
  const [isic4, setIsic4] = useState([]);
  const [additionalBuyerIdTypes, setAdditionalBuyerIdTypes] = useState([]);
  const [provinces, setProvinces] = useState([]);



useEffect(() => {
  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadCurrencies(),
        loadInvoiceTypes(),
        loadAdditionalBuyerIdTypes(),
        loadProvinces(),
        loadIsic4()
      ]);
    } catch (error) {
      console.error('Error loading lookup data:', error);
    } finally {
      setLoading(false);
    }
  };

  loadAllData();
}, []);

  const loadInvoiceTypes = async () => {
    try {
      const response = await getAllInvoiceTypes();
      const invoiceTypeData = Array.isArray(response) ? response :
                             (response.data ? response.data : []);

      // Map backend response to component format
      const mappedInvoiceTypes = invoiceTypeData.map(invoiceType => ({
        code: invoiceType.code,
        descriptionAr: invoiceType.arabicDescription,
        descriptionEn: invoiceType.englishDescription,
        xmlDigitReference: invoiceType.xmlDigitReference,
        allowedPercentage: invoiceType.allowedPercentage,
        enabled: invoiceType.enabled
      }));

      setInvoiceTypes(mappedInvoiceTypes);
    } catch (error) {
      console.error('Error loading invoice types:', error);
    }
  };

  // In LookupManagementContainer.js
const handleAddInvoiceType = async (invoiceTypeData) => {
  setLoading(true);
  try {
    // Map component data to backend format
    const backendData = {
      code: invoiceTypeData.code,
      arabicDescription: invoiceTypeData.descriptionAr,
      englishDescription: invoiceTypeData.descriptionEn,
      xmlDigitReference: invoiceTypeData.xmlDigitReference,
      allowedPercentage: invoiceTypeData.allowedPercentage
    };

    const response = await addNewInvoiceType(backendData);
    const responseData = response.data || response;

    // Map response to component format
    const mappedInvoiceType = {
      code: responseData.code,
      descriptionAr: responseData.arabicDescription,
      descriptionEn: responseData.englishDescription,
      xmlDigitReference: responseData.xmlDigitReference,
      allowedPercentage: responseData.allowedPercentage,
      enabled: responseData.enabled
    };

    setInvoiceTypes([...invoiceTypes, mappedInvoiceType]);
    return { success: true, data: mappedInvoiceType };
  } catch (error) {
    console.error('Error adding invoice type:', error);
    showToast({
      toastOptions: { type: "error" },
      data: {
        text: error.errorMessage ? (
          <div>{error.errorMessage}</div>
        ) : (
          <>
            {error.englishDescription && <div>{error.englishDescription}</div>}
            {error.arabicDescription && <div>{error.arabicDescription}</div>}
            {error.code && <div>{error.code}</div>}
          </>
        ),
      },
    });
    return { success: false, error }; // Pass the full error object
  } finally {
    setLoading(false);
  }
};

const handleUpdateInvoiceType = async (code, data) => {
  setLoading(true);
  try {
    // Map to backend format - this matches LuInvoiceTypeUpdateDto
    const backendData = {
      arabicDescription: data.descriptionAr,
      englishDescription: data.descriptionEn
      // Note: xmlDigitReference and allowedPercentage are not included in the update DTO
    };

    const response = await updateInvoiceType(code, backendData);
    const responseData = response.data || response;

    // Map response to frontend format
    const mappedInvoiceType = {
      code: responseData.code,
      descriptionAr: responseData.arabicDescription,
      descriptionEn: responseData.englishDescription,
      xmlDigitReference: responseData.xmlDigitReference,
      allowedPercentage: responseData.allowedPercentage,
      enabled: responseData.enabled
    };

    const updatedInvoiceTypes = invoiceTypes.map(t =>
      t.code === mappedInvoiceType.code ? mappedInvoiceType : t
    );

    setInvoiceTypes(updatedInvoiceTypes);
    return { success: true, data: mappedInvoiceType };
  } catch (error) {
    console.error('Error updating invoice type:', error);
    return { success: false, error };
  } finally {
    setLoading(false);
  }
};

const handleToggleInvoiceTypeStatus = async (code, enabled, note) => {
  setLoading(true);
  try {
    // Format the data to match LuDisableAndEnableDto
    const backendData = {
      enabled: enabled,
      note: note
    };

    const response = await toggleInvoiceTypeStatus(code, backendData);

    // Process response
    const responseData = response.data || response;

    // Map to component format
    const mappedInvoiceType = {
      code: responseData.code,
      descriptionAr: responseData.arabicDescription,
      descriptionEn: responseData.englishDescription,
      xmlDigitReference: responseData.xmlDigitReference,
      allowedPercentage: responseData.allowedPercentage,
      enabled: responseData.enabled
    };

    // Update state
    const updatedInvoiceTypes = invoiceTypes.map(t =>
      t.code === mappedInvoiceType.code ? mappedInvoiceType : t
    );

    setInvoiceTypes(updatedInvoiceTypes);
    return { success: true, data: mappedInvoiceType };
  } catch (error) {
    console.error('Error toggling invoice type status:', error);

    // Extract error message if available
    let errorMsg = 'Operation failed';
    if (error.errorMessage) {
      errorMsg = error.errorMessage;
    } else if (error.response && error.response.data && error.response.data.errorMessage) {
      errorMsg = error.response.data.errorMessage;
    }

    return { success: false, error, errorMessage: errorMsg };
  } finally {
    setLoading(false);
  }
};
  const loadCurrencies = async () => {
    try {
      const response = await getAllCurrencies();
      console.log('API response:', response);

      // The response itself is the array of currencies, not response.data
      const currencyData = Array.isArray(response) ? response :
                           (response.data ? response.data : []);

      console.log('Currency data to map:', currencyData);

      // Map the backend response to the format expected by the component
      const mappedCurrencies = currencyData.map(currency => ({
        currency: currency.currency,
        descriptionAr: currency.arabicDescription,
        descriptionEn: currency.englishDescription,
        enabled: currency.enabled,
        rate: currency.rate,
        rateDate: currency.rateDate
      }));

      console.log('Mapped currencies:', mappedCurrencies);
      setCurrencies(mappedCurrencies);
    } catch (error) {
      console.error('Error loading currencies:', error);
    }
  };
    const loadIsic4 = async () => {
    try {
      const response = await getAllIsic4();
      console.log('API response:', response);

      // The response itself is the array of currencies, not response.data
      const isic4Data = Array.isArray(response) ? response :
                           (response.data ? response.data : []);

      console.log('isic4Data data to map:', isic4Data);

      // Map the backend response to the format expected by the component
      const mappedIsic4 = isic4Data.map(isic4 => ({
        code: isic4.code,
        descriptionAr: isic4.arabicDescription,
        descriptionEn: isic4.englishDescription,
        enabled: isic4.enabled,
      }));

      console.log('Mapped isic4:', mappedIsic4);
      setIsic4(mappedIsic4);
    } catch (error) {
      console.error('Error loading isic4:', error);
    }
  };

  const handleAddCurrency = async (currencyData) => {
    setLoading(true);
    try {
      console.log("Currency data received in container:", currencyData);

      // Map the component data to the format expected by the backend
      const backendData = {
        currency: currencyData.currency,
        rate: currencyData.rate || 1,
        arabicDescription: currencyData.arabicDescription || currencyData.descriptionAr,
        englishDescription: currencyData.englishDescription || currencyData.descriptionEn,
        enabled: currencyData.enabled
      };

      console.log("Sending to API:", backendData);

      const response = await addNewCurrency(backendData);
      // Handle potential response formats
      const responseData = response.data || response;

      // Map the response back to the component format
      const mappedCurrency = {
        currency: responseData.currency,
        descriptionAr: responseData.arabicDescription,
        descriptionEn: responseData.englishDescription,
        enabled: responseData.enabled,
        rate: responseData.rate,
        rateDate: responseData.rateDate
      };

      setCurrencies([...currencies, mappedCurrency]);
      return { success: true, data: mappedCurrency };
    } catch (error) {
      console.error('Error adding currency:', error);
      showToast({
        toastOptions: { type: "error" },
        data: {
          text: error.errorMessage ? (
            <div>{error.errorMessage}</div>
          ) : (
            <>
              {error.englishDescription && <div>{error.englishDescription}</div>}
              {error.arabicDescription && <div>{error.arabicDescription}</div>}
              {error.code && <div>{error.code}</div>}
            </>
          ),
        },
      });
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };
   const handleAddIsic4 = async (isic4Data) => {
    setLoading(true);
    try {
      console.log("isic4 data received in container:", isic4Data);

      // Map the component data to the format expected by the backend
      const backendData = {
        code: isic4Data.code,
        arabicDescription: isic4Data.arabicDescription || isic4Data.descriptionAr,
        englishDescription: isic4Data.englishDescription || isic4Data.descriptionEn
      };

      console.log("Sending to API:", backendData);

      const response = await addNewIsic4(backendData);
      // Handle potential response formats
      const responseData = response.data || response;

      // Map the response back to the component format
      const mappedIsic4 = {
        code: responseData.code,
        descriptionAr: responseData.arabicDescription,
        descriptionEn: responseData.englishDescription,
        enabled: responseData.enabled
      };

      setIsic4([...isic4, mappedIsic4]);
      return { success: true, data: mappedIsic4 };
    } catch (error) {
      console.error('Error adding isic4:', error);
      showToast({
        toastOptions: { type: "error" },
        data: {
          text: error.errorMessage ? (
            <div>{error.errorMessage}</div>
          ) : (
            <>
              {error.englishDescription && <div>{error.englishDescription}</div>}
              {error.arabicDescription && <div>{error.arabicDescription}</div>}
              {error.code && <div>{error.code}</div>}
            </>
          ),
        },
      });
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };
  const handleUpdateCurrency = async (code, data) => {
    setLoading(true);
    try {
      // Map to backend format - this matches LuCurrencyUpdateDto
      const backendData = {
        rate: data.rate,
        arabicDescription: data.descriptionAr,
        englishDescription: data.descriptionEn
      };

      const response = await updateCurrency(code, backendData);
      // Handle the response which could be direct or in a data property
      const responseData = response.data || response;

      // Map response to frontend format
      const mappedCurrency = {
        currency: responseData.currency,
        descriptionAr: responseData.arabicDescription,
        descriptionEn: responseData.englishDescription,
        enabled: responseData.enabled,
        rate: responseData.rate,
        rateDate: responseData.rateDate
      };

      const updatedCurrencies = currencies.map(c =>
        c.currency === mappedCurrency.currency ? mappedCurrency : c
      );

      setCurrencies(updatedCurrencies);
      return { success: true, data: mappedCurrency };
    } catch (error) {
      console.error('Error updating currency:', error);
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };
  const handleUpdateIsic4 = async (code, data) => {
    setLoading(true);
    console.log("code codeeeeeeeeeeeeeeeeeeeeeeeee"+code)
    try {
      // Map to backend format - this matches LuCurrencyUpdateDto
      const backendData = {
        code: data.code,
        arabicDescription: data.descriptionAr,
        englishDescription: data.descriptionEn
      };

      const response = await updateIsic4(code, backendData);
      // Handle the response which could be direct or in a data property
      const responseData = response.data || response;

      // Map response to frontend format
      const mappedIsic4 = {
        code: responseData.code,
        descriptionAr: responseData.arabicDescription,
        descriptionEn: responseData.englishDescription
      };

      const updatedIsic4 = isic4.map(c =>
        c.code === mappedIsic4.code ? mappedIsic4 : c
      );

      setIsic4(updatedIsic4);
      return { success: true, data: mappedIsic4 };
    } catch (error) {
      console.error('Error updating isic4:', error);
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };

  const handleToggleCurrencyStatus = async (code, enabled, note) => {
    setLoading(true);
    try {
      // Create the required DTO format with both enabled status and note
      const backendData = {
        enabled: enabled,
        note: note
      };

      const response = await toggleCurrencyStatus(code, backendData);

      // Handle the response which could be direct or in a data property
      const responseData = response.data || response;

      // Map response to frontend format
      const mappedCurrency = {
        currency: responseData.currency,
        descriptionAr: responseData.arabicDescription,
        descriptionEn: responseData.englishDescription,
        enabled: responseData.enabled,
        rate: responseData.rate,
        rateDate: responseData.rateDate
      };

      const updatedCurrencies = currencies.map(c =>
        c.currency === mappedCurrency.currency ? mappedCurrency : c
      );

      setCurrencies(updatedCurrencies);
      return { success: true, data: mappedCurrency };
    } catch (error) {
      console.error('Error toggling currency status:', error);
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };

    const handleToggleIsic4Status = async (code, enabled, note) => {
    setLoading(true);
    try {
      // Create the required DTO format with both enabled status and note
      const backendData = {
        enabled: enabled,
        note: note
      };

      const response = await toggleIsic4Status(code, backendData);

      // Handle the response which could be direct or in a data property
      const responseData = response.data || response;

      // Map response to frontend format
      const mappedIsic4 = {
        code: responseData.code,
        descriptionAr: responseData.arabicDescription,
        descriptionEn: responseData.englishDescription,
        enabled: responseData.enabled,
      };

      const updatedIsic4 = isic4.map(c =>
        c.code === mappedIsic4.code ? mappedIsic4 : c
      );

      setIsic4(updatedIsic4);
      return { success: true, data: mappedIsic4 };
    } catch (error) {
      console.error('Error toggling Isic4 status:', error);
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };


// Add the load function
const loadAdditionalBuyerIdTypes = async () => {
  try {
    const response = await getAllAdditionalBuyerIdTypes();
    const additionalBuyerIdTypeData = Array.isArray(response) ? response :
                           (response.data ? response.data : []);

    // Map backend response to component format
    const mappedAdditionalBuyerIdTypes = additionalBuyerIdTypeData.map(type => ({
      code: type.code,
      descriptionAr: type.arabicDescription,
      descriptionEn: type.englishDescription,
      enabled: type.enabled
    }));

    setAdditionalBuyerIdTypes(mappedAdditionalBuyerIdTypes);
  } catch (error) {
    console.error('Error loading additional buyer ID types:', error);
  }
};

// Add handler functions
const handleAddAdditionalBuyerIdType = async (data) => {
  setLoading(true);
  try {
    // Map component data to backend format
    const backendData = {
      code: data.code,
      arabicDescription: data.descriptionAr,
      englishDescription: data.descriptionEn
    };

    const response = await addNewAdditionalBuyerIdType(backendData);
    const responseData = response.data || response;

    // Map response to component format
    const mappedType = {
      code: responseData.code,
      descriptionAr: responseData.arabicDescription,
      descriptionEn: responseData.englishDescription,
      enabled: responseData.enabled
    };

    setAdditionalBuyerIdTypes([...additionalBuyerIdTypes, mappedType]);
    return { success: true, data: mappedType };
  } catch (error) {
    console.error('Error adding additional buyer ID type:', error);
    return { success: false, error };
  } finally {
    setLoading(false);
  }
};

const handleUpdateAdditionalBuyerIdType = async (code, data) => {
  setLoading(true);
  try {
    // Map to backend format
    const backendData = {
      arabicDescription: data.descriptionAr,
      englishDescription: data.descriptionEn
    };

    const response = await updateAdditionalBuyerIdType(code, backendData);
    const responseData = response.data || response;

    // Map response to component format
    const mappedType = {
      code: responseData.code,
      descriptionAr: responseData.arabicDescription,
      descriptionEn: responseData.englishDescription,
      enabled: responseData.enabled
    };

    const updatedTypes = additionalBuyerIdTypes.map(t =>
      t.code === mappedType.code ? mappedType : t
    );

    setAdditionalBuyerIdTypes(updatedTypes);
    return { success: true, data: mappedType };
  } catch (error) {
    console.error('Error updating additional buyer ID type:', error);
    return { success: false, error };
  } finally {
    setLoading(false);
  }
};

const handleToggleAdditionalBuyerIdTypeStatus = async (code, enabled, note) => {
  setLoading(true);
  try {
    // Format the data to match LuDisableAndEnableDto
    const backendData = {
      enabled: enabled,
      note: note
    };

    const response = await toggleAdditionalBuyerIdTypeStatus(code, backendData);
    const responseData = response.data || response;

    // Map to component format
    const mappedType = {
      code: responseData.code,
      descriptionAr: responseData.arabicDescription,
      descriptionEn: responseData.englishDescription,
      enabled: responseData.enabled
    };

    // Update state
    const updatedTypes = additionalBuyerIdTypes.map(t =>
      t.code === mappedType.code ? mappedType : t
    );

    setAdditionalBuyerIdTypes(updatedTypes);
    return { success: true, data: mappedType };
  } catch (error) {
    console.error('Error toggling additional buyer ID type status:', error);

    // Extract error message if available
    let errorMsg = 'Operation failed';
    if (error.errorMessage) {
      errorMsg = error.errorMessage;
    } else if (error.response && error.response.data && error.response.data.errorMessage) {
      errorMsg = error.response.data.errorMessage;
    }

    return { success: false, error, errorMessage: errorMsg };
  } finally {
    setLoading(false);
  }
};



// Add the load function
const loadProvinces = async () => {
  try {
    const response = await getAllProvinces();
    const provinceData = Array.isArray(response) ? response :
                       (response.data ? response.data : []);

    // Map backend response to component format
    const mappedProvinces = provinceData.map(province => ({
      code: province.provinceCode,
      descriptionAr: province.provinceNameAr,
      descriptionEn: province.provinceNameEn
    }));

    setProvinces(mappedProvinces);
  } catch (error) {
    console.error('Error loading provinces:', error);
  }
};

// Add handler function for update
const handleUpdateProvince = async (code, data) => {
  setLoading(true);
  try {
    // Map to backend format
    const backendData = {
      arabicDescription: data.descriptionAr,
      englishDescription: data.descriptionEn
    };

    const response = await updateProvince(code, backendData);
    const responseData = response.data || response;

    // Map response to component format
    const mappedProvince = {
      code: responseData.provinceCode,
      descriptionAr: responseData.provinceNameAr,
      descriptionEn: responseData.provinceNameEn
    };

    const updatedProvinces = provinces.map(p =>
      p.code === mappedProvince.code ? mappedProvince : p
    );

    setProvinces(updatedProvinces);
    return { success: true, data: mappedProvince };
  } catch (error) {
    console.error('Error updating province:', error);
    return { success: false, error };
  } finally {
    setLoading(false);
  }
};

// Update the return statement to include the new props
return (
  <React.Fragment>
    {loading && <Loading />}
    <LookupManagementComponent
      tab={tab}
      currencies={currencies}
      isic4={isic4}
      invoiceTypes={invoiceTypes}
      additionalBuyerIdTypes={additionalBuyerIdTypes}
      provinces={provinces}
      onAddCurrency={handleAddCurrency}
      onAddIsic4={handleAddIsic4}
      onUpdateCurrency={handleUpdateCurrency}
      onUpdateIsic4={handleUpdateIsic4}
      onToggleCurrencyStatus={handleToggleCurrencyStatus}
      onToggleIsic4Status={handleToggleIsic4Status}
      onAddInvoiceType={handleAddInvoiceType}
      onUpdateInvoiceType={handleUpdateInvoiceType}
      onToggleInvoiceTypeStatus={handleToggleInvoiceTypeStatus}
      onAddAdditionalBuyerIdType={handleAddAdditionalBuyerIdType}
      onUpdateAdditionalBuyerIdType={handleUpdateAdditionalBuyerIdType}
      onToggleAdditionalBuyerIdTypeStatus={handleToggleAdditionalBuyerIdTypeStatus}
      onUpdateProvince={handleUpdateProvince}
    />
  </React.Fragment>
);
};

export default React.memo(LookupManagementContainer);
